import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function Login() {
  const { loginWithGoogle, loginMock, isFirebaseConfigured, authorizedEmail } = useAuth();
  const navigate = useNavigate();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onGoogle = async () => {
    setErr("");
    setBusy(true);
    try {
      await loginWithGoogle();
      navigate("/app", { replace: true });
    } catch (e) {
      setErr(e.message || "Errore login");
    } finally {
      setBusy(false);
    }
  };

  const onMock = () => {
    setErr("");
    loginMock();
    navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel — system context */}
      <div className="hidden md:flex flex-col justify-between border-r border-[#1F1F1F] p-10 bg-[#0C0C0C]">
        <Link to="/" className="font-heading font-black uppercase tracking-tighter text-xl">
          ORG3<span className="text-[#5C5C5C]">.ai</span>
        </Link>

        <div className="max-w-md">
          <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-neutral-500 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#F5A623]" strokeWidth={1.5} />
            <span>structura · cabina di regia</span>
          </div>
          <h2 className="font-heading uppercase text-3xl tracking-tight leading-tight mb-6">
            Lettura piena.
            <br />
            <span className="text-[#F5A623]">Scrittura con 2FA.</span>
          </h2>
          <p className="font-mono text-xs text-neutral-500 leading-relaxed">
            Livello 1 · accesso con Google (owner only). <br />
            Livello 2 · secondo fattore su ogni azione di scrittura (crea lead, genera preventivo, genera PRD).
          </p>
          <div
            data-testid="login-read-badge"
            className="mt-6 inline-flex items-center gap-2 border border-[#1F1F1F] bg-[#050505] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-300"
          >
            <span className="w-1.5 h-1.5 bg-[#EDEDED]"></span>
            badge · lettura piena
          </div>
        </div>

        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-600">
          {"// only invited emails are accepted"}
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex flex-col justify-center p-6 md:p-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-[#EDEDED] mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Torna alla landing
        </Link>

        <div
          data-testid={TID.loginBox}
          className="max-w-md border border-[#1F1F1F] bg-[#0C0C0C]"
        >
          <div className="border-b border-[#1F1F1F] px-6 py-4">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">
              org3://auth · tier 1
            </div>
            <h1 className="font-heading uppercase text-2xl tracking-tight">Entra nel sistema</h1>
          </div>

          <div className="px-6 py-8 space-y-4">
            <button
              data-testid={TID.loginGoogleBtn}
              onClick={onGoogle}
              disabled={busy}
              className="w-full flex items-center justify-center gap-3 bg-[#EDEDED] hover:bg-white text-black px-4 py-3 font-mono text-sm uppercase tracking-widest transition-colors disabled:opacity-50"
            >
              <GoogleGlyph />
              <span>{busy ? "Autenticazione…" : "Continua con Google"}</span>
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-[#1F1F1F]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">or</span>
              <div className="h-px flex-1 bg-[#1F1F1F]" />
            </div>

            <button
              data-testid={TID.loginMockBtn}
              onClick={onMock}
              className="w-full border border-[#1F1F1F] hover:border-[#333] hover:bg-[#141414] px-4 py-3 font-mono text-xs uppercase tracking-widest text-neutral-300"
            >
              Accedi in modalità scaffold
            </button>

            {err && (
              <div data-testid={TID.loginError} className="font-mono text-xs text-red-400 border border-red-900/60 bg-red-950/20 px-3 py-2">
                {err}
              </div>
            )}

            <div className="pt-4 border-t border-[#1F1F1F] space-y-1 font-mono text-[11px] text-neutral-500 leading-relaxed">
              <div>
                <span className="text-neutral-600">firebase:</span>{" "}
                <span className={isFirebaseConfigured ? "text-[#EDEDED]" : "text-[#F5A623]"}>
                  {isFirebaseConfigured ? "configured" : "not configured (scaffold mode)"}
                </span>
              </div>
              <div>
                <span className="text-neutral-600">authorized:</span>{" "}
                <span className="text-neutral-300">{authorizedEmail || "— (open)"}</span>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-6 max-w-md font-mono text-[10px] leading-relaxed text-neutral-600">
          Il secondo fattore verrà richiesto in-app solo quando eseguirai un&apos;azione irreversibile
          (es. <span className="text-neutral-400">Commit</span> su un loop).
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" className="w-4 h-4">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.3l6.7-6.7C35.7 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.4 13.1 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.5-.1-3-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17.5z"/>
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.6 0 20.2 0 24s.9 7.4 2.5 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.5-5.8c-2 1.4-4.7 2.3-7.7 2.3-6.3 0-11.6-3.6-13.6-8.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}
